import { Node as CraftNode, NodeHelpersType, useEditor } from '@candulabs/craft';
import React, { ReactElement } from 'react';

import { toCssSelector } from '@candu-ai/stylesheet/utils';
import { hiddenStyles } from 'commons/utils';
import Node from 'craft/components/Node/Node';
import Placeholder from 'craft/components/Placeholder';
import { BuiltInComponents } from 'craft/constants';
import { useNodeContext } from 'craft/hooks';
import { useFormAncestor } from 'craft/state';
import { hasDescendants } from 'craft/utils';

import { SelectCardGroupToolbox } from './toolbox/SelectCardGroupToolbox';
import { SelectCardToolbox } from './toolbox/SelectCardToolbox';

interface FormSingleSelectCardProps {
  children?: ReactElement | ReactElement[];
  value?: string;
}

export const FormSingleSelectCard = ({ children, value }: FormSingleSelectCardProps) => {
  const { id } = useNodeContext();
  const { shouldDisplayErrorState } = useFormAncestor(id);
  const hasChildren = hasDescendants(children);

  const { query } = useEditor();
  const parentNode = query.node(id).getParent();
  const { formInputId } = parentNode.get().data.props;
  const indexInParent = parentNode.childNodes().indexOf(id);

  const ssCardClassName = toCssSelector('form', {
    output: 'html',
    className: 'ssCard',
  });

  const classNames = `${ssCardClassName} ${shouldDisplayErrorState ? ' error' : ''}`.trim();

  return (
    <Node
      htmlFor={`${formInputId}-${indexInParent}`}
      is="label"
      data-cy="node-singleselectcard"
      className={classNames}
    >
      <input
        value={value}
        id={`${formInputId}-${indexInParent}`}
        name={formInputId}
        type="radio"
        disabled
        checked={indexInParent === 0}
        style={hiddenStyles as any}
      />
      {hasChildren ? children : <Placeholder minWidth={0} />}
    </Node>
  );
};

FormSingleSelectCard.craft = {
  name: BuiltInComponents.FormSingleSelectCard,
  displayName: (node: CraftNode, helpers: NodeHelpersType) => {
    const parentNode = node.data.parent;
    return `Single-select Card ${helpers(parentNode).childNodes().indexOf(node.id) + 1}`;
  },

  isCanvas: true,
  rules: {
    canDelete: (node: CraftNode, helpers: NodeHelpersType) => {
      const isLastRemaining = helpers(node.id).getParent().descendants().length === 1;
      return !isLastRemaining;
    },
    canDrop: (target: CraftNode) =>
      target.data.name === BuiltInComponents.FormSingleSelectCardGroup,
  },
};

FormSingleSelectCard.toolbox = {
  component: [SelectCardToolbox, SelectCardGroupToolbox],
};

FormSingleSelectCard.selectedToolbar = {};
